"use server";

import type { Prisma } from "@prisma/client";
import { z } from "zod";
import type { Access } from "@/lib/access-rules";
import { cleanCommentText } from "@/lib/article-comments";
import { defineAction } from "@/lib/define-action";
import { bestEffort } from "@/lib/errors";
import { mentionedIn } from "@/lib/mentions";
import { notify } from "@/lib/notify";
import { prisma } from "@/lib/prisma";
import { loadRights } from "./access";
import { statusProblem, STATUS_LABEL, WAITING_LABEL, WAITING_REASONS, type WaitingReasonKey } from "./card-rules";
import type { CardDetail } from "./card-types";
import { loadDeliverableDetail } from "./deliverable-detail";
import { deliverableBlocksDone } from "./deliverable-rules";
import { parseDay } from "./schedule";
import { assetReferenceSchema } from "./template-schema";

const GUARD = "projects:view";
const id = z.number().int().positive();
const day = z.string().refine((v) => parseDay(v) !== null, "Choose a valid date.");
const COMMENT_MAX = 5000;
const fail = (error: string) => ({ ok: false as const, error });
const pathsOf = (assets: unknown) => (Array.isArray(assets) ? (assets as { path: string }[]).map((a) => a.path).join("|") : "");
const dayKey = (d: Date | null) => (d ? d.toISOString().slice(0, 10) : null);

type Ctx = { userId: number; access: Access };

async function loadCard(ctx: Ctx, projectId: number, cardId: number) {
  const found = await loadRights(ctx.access, projectId);
  if (!found) return { error: "This project no longer exists." } as const;
  const card = await prisma.projectCard.findFirst({
    where: { id: cardId, projectId },
    select: { id: true, title: true, status: true, bucketId: true, assignees: { select: { userId: true } } },
  });
  if (!card) return { error: "This task no longer exists." } as const;
  const assigned = card.assignees.some((a) => a.userId === ctx.userId);
  const closed = found.project.status === "CLOSED";
  return {
    error: null,
    project: found.project,
    card,
    canEdit: found.rights.canEdit,
    canUpdate: found.rights.canEdit || (found.rights.canWork && assigned),
    canComment: found.rights.canWork,
    readOnlyReason: closed ? "A closed project is read-only. A PM admin can reopen it." : null,
  } as const;
}

const PRIORITY_LABEL = { URGENT: "Urgent", IMPORTANT: "Important", MEDIUM: "Medium", LOW: "Low" } as const;
const PREVIEW_LABEL = { NONE: "none", CHECKLIST: "checklist", DESCRIPTION: "description" } as const;

const log = (tx: Prisma.TransactionClient | typeof prisma, projectId: number, cardId: number, userId: number, summary: string) =>
  tx.cardActivity.create({ data: { projectId, cardId, userId, summary: summary.slice(0, 500) } });

// No revalidatePath here on purpose: the board and the card dialog update their own state, and a revalidating server action would re-render the whole project page behind them. Views that list cards refresh themselves (CardSheetHost, MyTasksList).

export const getCardDetail = defineAction({
  guard: GUARD,
  safe: "Could not open the task.",
  input: z.object({ projectId: id, cardId: id }),
  run: async (ctx, input: { projectId: number; cardId: number }) => {
    const c = await loadCard(ctx, input.projectId, input.cardId);
    if (c.error !== null) return fail(c.error);
    const [card, logged] = await Promise.all([
      prisma.projectCard.findUniqueOrThrow({
        where: { id: input.cardId },
        select: {
          id: true, projectId: true, title: true, description: true, status: true, priority: true, preview: true, bucketId: true, effortHours: true, startDate: true, dueDate: true, assets: true, waivedNote: true,
          deliverableKind: true, deliverableName: true, deliverableRoleLabelId: true,
          deliverable: { select: { file: true, url: true, articleId: true, signedByName: true, providedBy: true, providedAt: true } },
          roles: { select: { roleLabelId: true } },
          assignees: { select: { userId: true } },
          waits: { orderBy: { startedAt: "asc" }, select: { reason: true, detail: true, startedAt: true, endedAt: true } },
          checklist: { orderBy: { position: "asc" }, select: { id: true, title: true, done: true } },
          comments: { where: { deletedAt: null }, orderBy: { createdAt: "asc" }, select: { id: true, userId: true, body: true, assets: true, createdAt: true, editedAt: true } },
          activity: { orderBy: { createdAt: "desc" }, take: 50, select: { id: true, userId: true, summary: true, createdAt: true } },
        },
      }),
      prisma.timeEntry.aggregate({ where: { cardId: input.cardId, deletedAt: null }, _sum: { minutes: true } }),
    ]);
    const userIds = [...new Set([...card.comments.map((m) => m.userId), ...card.activity.flatMap((a) => (a.userId ? [a.userId] : []))])];
    const users = await prisma.user.findMany({ where: { id: { in: userIds } }, select: { id: true, name: true } });
    const nameOf = new Map(users.map((u) => [u.id, u.name]));
    const open = !c.readOnlyReason;
    const deliverable = await loadDeliverableDetail(card, { id: c.project.id, clientId: c.project.clientId }, ctx.userId);
    const detail: CardDetail = {
      id: card.id,
      projectId: card.projectId,
      title: card.title,
      description: card.description ?? "",
      status: card.status,
      priority: card.priority,
      preview: card.preview,
      bucketId: card.bucketId,
      effortHours: card.effortHours === null ? null : Number(card.effortHours),
      loggedHours: Math.round(((logged._sum.minutes ?? 0) / 60) * 100) / 100,
      startDate: dayKey(card.startDate),
      dueDate: dayKey(card.dueDate),
      roleLabelIds: card.roles.map((r) => r.roleLabelId),
      assigneeIds: card.assignees.map((a) => a.userId),
      assets: Array.isArray(card.assets) ? (card.assets as CardDetail["assets"]) : [],
      waivedNote: card.waivedNote,
      waits: card.waits.map((w) => ({ reason: w.reason, detail: w.detail, startedAt: w.startedAt.toISOString(), endedAt: w.endedAt?.toISOString() ?? null })),
      checklist: card.checklist,
      comments: card.comments.map((m) => ({ id: m.id, userId: m.userId, userName: nameOf.get(m.userId) ?? "Former user", body: m.body, assets: Array.isArray(m.assets) ? (m.assets as CardDetail["assets"]) : [], createdAt: m.createdAt.toISOString(), edited: !!m.editedAt })),
      activity: card.activity.map((a) => ({ id: a.id, who: a.userId ? (nameOf.get(a.userId) ?? "Former user") : "System", summary: a.summary, at: a.createdAt.toISOString() })),
      deliverable,
      canEdit: open && c.canEdit,
      canUpdate: open && c.canUpdate,
      canComment: open && c.canComment,
      userId: ctx.userId,
    };
    return { ok: true as const, card: detail };
  },
});

const patchSchema = z.object({
  projectId: id,
  cardId: id,
  title: z.string().trim().min(1, "Give the task a title.").max(255).optional(),
  description: z.string().trim().max(10_000).optional(),
  effortHours: z.number().min(0).max(10_000).multipleOf(0.25, "Use steps of 15 minutes (0.25 h).").nullable().optional(),
  startDate: day.nullable().optional(),
  dueDate: day.nullable().optional(),
  roleLabelIds: z.array(id).max(20).optional(),
  assigneeIds: z.array(id).max(30).optional(),
  assets: z.array(assetReferenceSchema).max(20).optional(),
  bucketId: id.optional(),
  priority: z.enum(["URGENT", "IMPORTANT", "MEDIUM", "LOW"]).optional(),
  preview: z.enum(["NONE", "CHECKLIST", "DESCRIPTION"]).optional(),
});

export const updateCard = defineAction({
  guard: GUARD,
  safe: "Could not save the task.",
  input: patchSchema,
  run: async (ctx, input: z.infer<typeof patchSchema>) => {
    const c = await loadCard(ctx, input.projectId, input.cardId);
    if (c.error !== null) return fail(c.error);
    if (c.readOnlyReason) return fail(c.readOnlyReason);
    if (!c.canEdit) return fail("Only the project's managers can edit tasks.");

    const before = await prisma.projectCard.findUniqueOrThrow({
      where: { id: input.cardId },
      select: { title: true, description: true, effortHours: true, startDate: true, dueDate: true, bucketId: true, priority: true, preview: true, roles: { select: { roleLabelId: true } }, assignees: { select: { userId: true } }, assets: true },
    });
    const startDate = input.startDate === undefined ? before.startDate : input.startDate && parseDay(input.startDate);
    const dueDate = input.dueDate === undefined ? before.dueDate : input.dueDate && parseDay(input.dueDate);
    if (startDate && dueDate && dueDate < startDate) return fail("The due date cannot be before the start date.");

    if (input.roleLabelIds?.length && (await prisma.roleLabel.count({ where: { id: { in: input.roleLabelIds } } })) !== new Set(input.roleLabelIds).size) return fail("A role no longer exists. Reload and try again.");
    const assigneeIds = input.assigneeIds ? [...new Set(input.assigneeIds)] : undefined;
    const people = assigneeIds?.length
      ? await prisma.user.findMany({ where: { id: { in: assigneeIds }, kind: "MEMBER", status: "ACTIVE", deletedAt: null, blockedAt: null }, select: { id: true, name: true, email: true } })
      : [];
    if (assigneeIds && people.length !== assigneeIds.length) return fail("One of the people you chose is not an active eVia user.");
    let bucketName: string | null = null;
    if (input.bucketId && input.bucketId !== before.bucketId) {
      const to = await prisma.projectBucket.findFirst({ where: { id: input.bucketId, projectId: input.projectId }, select: { name: true } });
      if (!to) return fail("This bucket no longer exists.");
      bucketName = to.name;
    }

    const said: string[] = [];
    const data: Prisma.ProjectCardUpdateInput = {};
    const set = (patch: Prisma.ProjectCardUpdateInput, summary: string) => {
      Object.assign(data, patch);
      said.push(summary);
    };
    if (input.title !== undefined && input.title !== before.title) set({ title: input.title }, `Renamed the task to "${input.title}"`);
    if (input.description !== undefined && input.description !== (before.description ?? "")) set({ description: input.description || null }, "Edited the description");
    if (input.effortHours !== undefined && input.effortHours !== (before.effortHours === null ? null : Number(before.effortHours))) set({ effortHours: input.effortHours }, `Effort estimate: ${input.effortHours === null ? "none" : `${input.effortHours} h`}`);
    if (input.startDate !== undefined && dayKey(startDate || null) !== dayKey(before.startDate)) set({ startDate: startDate || null }, `Start date: ${dayKey(startDate || null) ?? "none"}`);
    if (input.dueDate !== undefined && dayKey(dueDate || null) !== dayKey(before.dueDate)) set({ dueDate: dueDate || null }, `Due date: ${dayKey(dueDate || null) ?? "none"}`);
    if (input.priority !== undefined && input.priority !== before.priority) set({ priority: input.priority }, `Priority: ${PRIORITY_LABEL[input.priority]}`);
    if (input.preview !== undefined && input.preview !== before.preview) set({ preview: input.preview }, `Task preview: ${PREVIEW_LABEL[input.preview]}`);
    if (input.assets !== undefined && pathsOf(input.assets) !== pathsOf(before.assets)) set({ assets: input.assets as unknown as Prisma.InputJsonValue }, "Changed the attachments");

    const oldRoles = new Set(before.roles.map((r) => r.roleLabelId));
    const rolesChanged = input.roleLabelIds && (input.roleLabelIds.length !== oldRoles.size || input.roleLabelIds.some((r) => !oldRoles.has(r)));
    const oldPeople = new Set(before.assignees.map((a) => a.userId));
    const added = assigneeIds?.filter((u) => !oldPeople.has(u)) ?? [];
    const removed = assigneeIds ? [...oldPeople].filter((u) => !assigneeIds.includes(u)) : [];
    if (rolesChanged) said.push("Changed the role labels");
    if (added.length || removed.length) {
      const names = await prisma.user.findMany({ where: { id: { in: [...added, ...removed] } }, select: { id: true, name: true } });
      const nameOf = (u: number) => names.find((n) => n.id === u)?.name ?? "Former user";
      if (added.length) said.push(`Assigned ${added.map(nameOf).join(", ")}`);
      if (removed.length) said.push(`Unassigned ${removed.map(nameOf).join(", ")}`);
    }
    if (bucketName) said.push(`Moved to ${bucketName}`);
    if (said.length === 0) return { ok: true as const, changed: false };

    await prisma.$transaction(async (tx) => {
      if (bucketName) {
        const last = await tx.projectCard.aggregate({ where: { bucketId: input.bucketId }, _max: { position: true } });
        await tx.projectCard.update({ where: { id: input.cardId }, data: { ...data, bucket: { connect: { id: input.bucketId } }, position: (last._max.position ?? -1) + 1 } });
      } else if (Object.keys(data).length) {
        await tx.projectCard.update({ where: { id: input.cardId }, data });
      }
      if (rolesChanged) {
        await tx.projectCardRole.deleteMany({ where: { cardId: input.cardId } });
        await tx.projectCardRole.createMany({ data: [...new Set(input.roleLabelIds)].map((roleLabelId) => ({ cardId: input.cardId, roleLabelId })) });
      }
      if (removed.length) await tx.projectCardAssignee.deleteMany({ where: { cardId: input.cardId, userId: { in: removed } } });
      if (added.length) await tx.projectCardAssignee.createMany({ data: added.map((userId) => ({ cardId: input.cardId, userId })) });
      await tx.cardActivity.createMany({ data: said.map((summary) => ({ projectId: input.projectId, cardId: input.cardId, userId: ctx.userId, summary: summary.slice(0, 500) })) });
    });

    const toTell = people.filter((p) => added.includes(p.id) && p.id !== ctx.userId);
    if (toTell.length) {
      await bestEffort(
        notify({
          type: "project_card_assigned",
          category: "projects",
          recipients: toTell.map((p) => ({ id: p.id, email: p.email, name: p.name ?? p.email })),
          message: `You were assigned "${input.title ?? before.title}" on "${c.project.name}".`,
          link: `/projects/${input.projectId}?card=${input.cardId}`,
        }),
        "tell the new assignees",
      );
    }
    return { ok: true as const, changed: true };
  },
});

const statusSchema = z.object({
  projectId: id,
  cardId: id,
  status: z.enum(["NOT_STARTED", "IN_PROGRESS", "WAITING", "DONE", "WAIVED"]),
  reason: z.enum(WAITING_REASONS).nullable().optional(),
  detail: z.string().trim().max(500).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});

export const setCardStatus = defineAction({
  guard: GUARD,
  safe: "Could not change the status.",
  input: statusSchema,
  run: async (ctx, input: z.infer<typeof statusSchema>) => {
    const c = await loadCard(ctx, input.projectId, input.cardId);
    if (c.error !== null) return fail(c.error);
    if (c.readOnlyReason) return fail(c.readOnlyReason);
    if (!c.canUpdate) return fail("You can only change the status of tasks assigned to you.");
    const problem = statusProblem(input);
    if (problem) return fail(problem);
    if (c.card.status === input.status && input.status !== "WAITING") return { ok: true as const, changed: false };
    if (input.status === "DONE") {
      const d = await prisma.projectCard.findUniqueOrThrow({ where: { id: input.cardId }, select: { deliverableKind: true, deliverable: { select: { id: true } } } });
      const blocked = deliverableBlocksDone({ required: !!d.deliverableKind, provided: !!d.deliverable });
      if (blocked) return fail(blocked);
    }

    const now = new Date();
    await prisma.$transaction(async (tx) => {
      await tx.cardWaitPeriod.updateMany({ where: { cardId: input.cardId, endedAt: null }, data: { endedAt: now } });
      if (input.status === "WAITING") {
        await tx.cardWaitPeriod.create({ data: { cardId: input.cardId, reason: input.reason as WaitingReasonKey, detail: input.detail || null, startedAt: now } });
      }
      await tx.projectCard.update({
        where: { id: input.cardId },
        data: { status: input.status, doneAt: input.status === "DONE" ? now : null, waivedNote: input.status === "WAIVED" ? input.note : null },
      });
      const extra = input.status === "WAITING" ? ` (${WAITING_LABEL[input.reason as WaitingReasonKey].toLowerCase()}${input.detail ? `: ${input.detail}` : ""})` : input.status === "WAIVED" ? `: ${input.note}` : "";
      await log(tx, input.projectId, input.cardId, ctx.userId, `Status ${STATUS_LABEL[c.card.status]} to ${STATUS_LABEL[input.status]}${extra}`);
    });
    return { ok: true as const, changed: true };
  },
});

const itemSchema = z.object({ projectId: id, cardId: id, itemId: id });

export const toggleChecklistItem = defineAction({
  guard: GUARD,
  safe: "Could not update the checklist.",
  input: itemSchema.extend({ done: z.boolean() }),
  run: async (ctx, input: { projectId: number; cardId: number; itemId: number; done: boolean }) => {
    const c = await loadCard(ctx, input.projectId, input.cardId);
    if (c.error !== null) return fail(c.error);
    if (c.readOnlyReason) return fail(c.readOnlyReason);
    if (!c.canUpdate) return fail("You can only tick items on tasks assigned to you.");
    const item = await prisma.projectChecklistItem.findFirst({ where: { id: input.itemId, cardId: input.cardId }, select: { title: true, done: true } });
    if (!item) return fail("This checklist item no longer exists.");
    if (item.done === input.done) return { ok: true as const, title: item.title };
    await prisma.$transaction([
      prisma.projectChecklistItem.update({ where: { id: input.itemId }, data: { done: input.done } }),
      log(prisma, input.projectId, input.cardId, ctx.userId, `${input.done ? "Ticked" : "Unticked"} "${item.title}"`),
    ]);
    return { ok: true as const, title: item.title };
  },
});

export const addChecklistItem = defineAction({
  guard: GUARD,
  safe: "Could not add the item.",
  input: z.object({ projectId: id, cardId: id, title: z.string().trim().min(1, "Write the step.").max(500) }),
  run: async (ctx, input: { projectId: number; cardId: number; title: string }) => {
    const c = await loadCard(ctx, input.projectId, input.cardId);
    if (c.error !== null) return fail(c.error);
    if (c.readOnlyReason) return fail(c.readOnlyReason);
    if (!c.canEdit) return fail("Only the project's managers can change the checklist.");
    const last = await prisma.projectChecklistItem.aggregate({ where: { cardId: input.cardId }, _max: { position: true } });
    await prisma.projectChecklistItem.create({ data: { cardId: input.cardId, title: input.title, position: (last._max.position ?? -1) + 1 } });
    await log(prisma, input.projectId, input.cardId, ctx.userId, `Added checklist item "${input.title}"`);
    return { ok: true as const };
  },
});

export const renameChecklistItem = defineAction({
  guard: GUARD,
  safe: "Could not save the item.",
  input: itemSchema.extend({ title: z.string().trim().min(1, "Write the step.").max(500) }),
  run: async (ctx, input: { projectId: number; cardId: number; itemId: number; title: string }) => {
    const c = await loadCard(ctx, input.projectId, input.cardId);
    if (c.error !== null) return fail(c.error);
    if (c.readOnlyReason) return fail(c.readOnlyReason);
    if (!c.canEdit) return fail("Only the project's managers can change the checklist.");
    const done = await prisma.projectChecklistItem.updateMany({ where: { id: input.itemId, cardId: input.cardId }, data: { title: input.title } });
    return done.count ? { ok: true as const } : fail("This checklist item no longer exists.");
  },
});

export const removeChecklistItem = defineAction({
  guard: GUARD,
  safe: "Could not remove the item.",
  input: itemSchema,
  run: async (ctx, input: { projectId: number; cardId: number; itemId: number }) => {
    const c = await loadCard(ctx, input.projectId, input.cardId);
    if (c.error !== null) return fail(c.error);
    if (c.readOnlyReason) return fail(c.readOnlyReason);
    if (!c.canEdit) return fail("Only the project's managers can change the checklist.");
    const item = await prisma.projectChecklistItem.findFirst({ where: { id: input.itemId, cardId: input.cardId }, select: { title: true } });
    if (!item) return fail("This checklist item no longer exists.");
    await prisma.projectChecklistItem.delete({ where: { id: input.itemId } });
    await log(prisma, input.projectId, input.cardId, ctx.userId, `Removed checklist item "${item.title}"`);
    return { ok: true as const };
  },
});

export const deleteCard = defineAction({
  guard: GUARD,
  safe: "Could not delete the task.",
  input: z.object({ projectId: id, cardId: id }),
  run: async (ctx, input: { projectId: number; cardId: number }) => {
    const c = await loadCard(ctx, input.projectId, input.cardId);
    if (c.error !== null) return fail(c.error);
    if (c.readOnlyReason) return fail(c.readOnlyReason);
    if (!c.canEdit) return fail("Only the project's managers can delete tasks.");
    const logged = await prisma.timeEntry.count({ where: { cardId: input.cardId, deletedAt: null } });
    if (logged > 0) return fail("Time is logged on this task, so it cannot be deleted. Mark it Skipped instead.");
    await prisma.projectCard.delete({ where: { id: input.cardId } });
    return { ok: true as const };
  },
});

const commentSchema = z.object({
  projectId: id,
  cardId: id,
  body: z.string().max(COMMENT_MAX * 2),
  assets: z.array(assetReferenceSchema).max(10).default([]),
});

function checked(body: string): string | { ok: false; error: string } {
  const text = cleanCommentText(body);
  if (!text) return fail("Write something first.");
  if (text.length > COMMENT_MAX) return fail(`Comments are limited to ${COMMENT_MAX} characters.`);
  return text;
}

export const postCardComment = defineAction({
  guard: GUARD,
  safe: "Could not post the comment.",
  input: commentSchema,
  run: async (ctx, input: z.infer<typeof commentSchema>) => {
    const c = await loadCard(ctx, input.projectId, input.cardId);
    if (c.error !== null) return fail(c.error);
    if (c.readOnlyReason) return fail(c.readOnlyReason);
    if (!c.canComment) return fail("Only people on the project can comment.");
    const text = checked(input.body);
    if (typeof text !== "string") return text;
    await prisma.cardComment.create({ data: { cardId: input.cardId, userId: ctx.userId, body: text, assets: input.assets.length ? (input.assets as unknown as Prisma.InputJsonValue) : undefined } });
    await log(prisma, input.projectId, input.cardId, ctx.userId, input.assets.length ? `Commented with ${input.assets.length} attachment${input.assets.length === 1 ? "" : "s"}` : "Commented");
    await bestEffort(notifyMentions(ctx.userId, input.projectId, input.cardId, c.card.title, c.project.name, text), "notify mentioned people");
    return { ok: true as const };
  },
});

async function notifyMentions(authorId: number, projectId: number, cardId: number, cardTitle: string, projectName: string, text: string) {
  const mentioned = await mentionedIn(text, authorId);
  if (mentioned.length === 0) return;
  const author = await prisma.user.findUnique({ where: { id: authorId }, select: { name: true } });
  const { getAccess } = await import("@/lib/access");
  const { visibleProjectsWhere } = await import("./access");
  const hearers: typeof mentioned = [];
  for (const u of mentioned) {
    const access = await getAccess(u.id);
    if (access && (await prisma.hubProject.count({ where: { AND: [{ id: projectId }, visibleProjectsWhere(access)] } })) > 0) hearers.push(u);
  }
  if (hearers.length === 0) return;
  await notify({
    type: "project_card_mention",
    category: "projects",
    recipients: hearers.map((u) => ({ id: u.id, email: u.email, name: u.name ?? u.email })),
    message: `${author?.name ?? "Someone"} mentioned you on "${cardTitle}" in ${projectName}: ${text.replace(/\s+/g, " ").slice(0, 100)}`,
    link: `/projects/${projectId}?card=${cardId}`,
  });
}

export const editCardComment = defineAction({
  guard: GUARD,
  safe: "Could not save the comment.",
  input: z.object({ projectId: id, cardId: id, commentId: id, body: z.string().max(COMMENT_MAX * 2) }),
  run: async (ctx, input: { projectId: number; cardId: number; commentId: number; body: string }) => {
    const c = await loadCard(ctx, input.projectId, input.cardId);
    if (c.error !== null) return fail(c.error);
    if (c.readOnlyReason) return fail(c.readOnlyReason);
    const text = checked(input.body);
    if (typeof text !== "string") return text;
    const done = await prisma.cardComment.updateMany({ where: { id: input.commentId, cardId: input.cardId, userId: ctx.userId, deletedAt: null }, data: { body: text, editedAt: new Date() } });
    return done.count ? { ok: true as const } : fail("You can only edit your own comments.");
  },
});

export const deleteCardComment = defineAction({
  guard: GUARD,
  safe: "Could not delete the comment.",
  input: z.object({ projectId: id, cardId: id, commentId: id }),
  run: async (ctx, input: { projectId: number; cardId: number; commentId: number }) => {
    const c = await loadCard(ctx, input.projectId, input.cardId);
    if (c.error !== null) return fail(c.error);
    if (c.readOnlyReason) return fail(c.readOnlyReason);
    const done = await prisma.cardComment.updateMany({
      where: { id: input.commentId, cardId: input.cardId, deletedAt: null, ...(c.canEdit ? {} : { userId: ctx.userId }) },
      data: { deletedAt: new Date() },
    });
    return done.count ? { ok: true as const } : fail("You can only delete your own comments.");
  },
});

/** The package time on this project is booked to, for the "Log time for this?" suggestion. */
export const getLogTimeTarget = defineAction({
  guard: GUARD,
  safe: "Could not prepare the time entry.",
  input: z.object({ projectId: id }),
  run: async (ctx, input: { projectId: number }) => {
    const found = await loadRights(ctx.access, input.projectId);
    if (!found) return fail("This project no longer exists.");
    return { ok: true as const, workPackageId: found.project.workPackageId, projectName: found.project.name };
  },
});
